/* ===================== CREATE LIVE CLASS MODAL =====================
   Popup form so instructors can schedule a live class without leaving
   the dashboard — POST /live-classes. Per the API: title + start_time +
   end_time required (start must be now or later, end after start);
   description and the record flag are optional. A parent course is REQUIRED
   in this form — every class must belong to one of the instructor's own
   courses — even though the API itself also accepts standalone classes. The
   backend generates the VideoSDK meeting id itself.

   Same form idiom as the My Profile tab: react-hook-form (@astro/forms),
   the dashboard's Field/input skin, and Laravel 422 field errors mapped
   inline via setError. The course dropdown is a react-select (never a
   native <select> — house rule) in the shared selectStyles skin, fed by the
   instructor's own courses (useInstructorCourses). Success invalidates
   ['dashboard','live-classes'] so the overview's stats and list refresh
   immediately, then closes. */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Select from 'react-select'
import { api } from '@astro/api'
import { Controller, useForm } from '@astro/forms'
import { DateTimeField } from '@astro/date-field'
import { selectStyles } from '@astro/astrologer-forms'
import { useInstructorCourses } from '../useDashboard'

const inputCls =
  'w-full bg-stone-50 border rounded-xl px-4 py-3 text-sm text-astroDark focus:outline-none focus:border-goldAccent/50 focus:bg-white transition-colors'

function Field({ label, error, className = '', children }) {
  return (
    <div className={className}>
      <label className="block text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5">{label}</label>
      {children}
      {error && <p className="text-[10px] text-red-500 mt-1">{error}</p>}
    </div>
  )
}

/** "YYYY-MM-DDTHH:mm" (datetime-local) → "YYYY-MM-DD HH:mm:00" (API). */
const toApiDateTime = (v) => (v ? `${String(v).replace('T', ' ')}:00` : '')

/** The current minute in datetime-local form — for min= and the
 * not-in-the-past rule. Same-format strings compare lexicographically. */
function nowLocal() {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export default function CreateLiveClassModal({ onClose, course = null }) {
  const queryClient = useQueryClient()
  const [banner, setBanner] = useState(null)

  const {
    register,
    handleSubmit,
    setError,
    getValues,
    control,
    formState: { errors },
  } = useForm({
    // `course` (optional) pre-selects the parent course when the modal is
    // opened from that course's details page.
    defaultValues: { title: '', description: '', course_id: course?.id ?? '', start_time: '', end_time: '', is_recorded: false },
    mode: 'onTouched',
  })

  // Lock the page scroll and close on Escape while the dialog is up.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  // Only the courses this instructor teaches (same instructor-scoped source as
  // the Academy tab's By Course section) — every live class must belong to one.
  const { courses, isLoading: coursesLoading } = useInstructorCourses()
  const courseOptions = courses.map((c) => ({ value: c.id, label: c.title }))

  const createMutation = useMutation({
    mutationFn: (payload) => api.liveClasses.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'live-classes'] })
      onClose()
    },
    onError: (err) => {
      // Laravel 422s carry per-field messages under `details`; field names
      // match the payload keys 1:1, so they surface inline as-is.
      const details = err?.details
      if (details && typeof details === 'object') {
        for (const [key, msgs] of Object.entries(details)) {
          setError(key, { type: 'server', message: Array.isArray(msgs) ? msgs[0] : String(msgs) })
        }
      }
      setBanner(err?.message || 'Could not schedule the class. Please try again.')
    },
  })

  const onSubmit = handleSubmit((v) => {
    setBanner(null)
    const payload = {
      title: v.title.trim(),
      start_time: toApiDateTime(v.start_time),
      end_time: toApiDateTime(v.end_time),
      // Required by this form — the `required` rule guarantees a real id here,
      // so it can never post course_id: 0 and trip the API's exists rule.
      course_id: Number(v.course_id),
    }
    if (v.description?.trim()) payload.description = v.description.trim()
    if (v.is_recorded) payload.is_recorded = true
    createMutation.mutate(payload)
  })

  const cls = (name) => `${inputCls} ${errors[name] ? 'border-red-300' : 'border-stone-200'}`
  const minStart = nowLocal()

  const modal = (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      {/* Click-away backdrop in the site's maroon, matching the hero glow. */}
      <div className="absolute inset-0 bg-[#3D0A0A]/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Create Live Class"
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6"
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <span className="eyebrow text-amber-700">Vedic Academy</span>
            <h3 className="text-xl text-astroDark font-royal">Create Live Class</h3>
            <p className="text-xs text-stone-500 mt-0.5">Schedule a session — the meeting room is generated automatically.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 shrink-0 rounded-full border border-stone-200 text-stone-500 hover:bg-stone-50 flex items-center justify-center text-sm font-bold"
          >
            ✕
          </button>
        </div>

        {banner && (
          <div className="mb-4 px-4 py-3 rounded-xl text-xs font-semibold bg-red-50 text-red-600 border border-red-100">
            {banner}
          </div>
        )}

        <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Class Title" error={errors.title?.message} className="sm:col-span-2">
            <input
              className={cls('title')}
              placeholder="e.g. Vedic Astrology Basics"
              {...register('title', { required: 'Class title is required' })}
            />
          </Field>

          <Field label="Course" error={errors.course_id?.message} className="sm:col-span-2">
            <Controller
              name="course_id"
              control={control}
              rules={{ required: 'Please choose the course this class belongs to' }}
              render={({ field }) => (
                <Select
                  inputId="live-class-course"
                  options={courseOptions}
                  value={courseOptions.find((o) => String(o.value) === String(field.value)) ?? null}
                  onChange={(option) => field.onChange(option?.value ?? '')}
                  onBlur={field.onBlur}
                  placeholder={coursesLoading ? 'Loading your courses…' : 'Select a course'}
                  styles={selectStyles}
                  isLoading={coursesLoading}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              )}
            />
          </Field>

          <Field label="Starts At" error={errors.start_time?.message}>
            <Controller
              name="start_time"
              control={control}
              rules={{
                required: 'Start time is required',
                validate: (v) => v >= minStart || 'Start time must be now or later',
              }}
              render={({ field }) => (
                <DateTimeField
                  min={minStart}
                  className={cls('start_time')}
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            />
          </Field>

          <Field label="Ends At" error={errors.end_time?.message}>
            <Controller
              name="end_time"
              control={control}
              rules={{
                required: 'End time is required',
                validate: (v) => {
                  const start = getValues('start_time')
                  return !start || v > start || 'End time must be after the start time'
                },
              }}
              render={({ field }) => (
                <DateTimeField
                  min={minStart}
                  className={cls('end_time')}
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            />
          </Field>

          <Field label="Description (optional)" error={errors.description?.message} className="sm:col-span-2">
            <textarea
              rows={3}
              className={`${cls('description')} resize-none`}
              placeholder="What will this session cover?"
              {...register('description')}
            />
          </Field>

          <label className="sm:col-span-2 flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-amber-600" {...register('is_recorded')} />
            <span className="text-sm text-astroDark font-semibold">Record this class</span>
            <span className="text-[10px] text-stone-400 ml-auto">Recording appears after the session ends</span>
          </label>

          <div className="sm:col-span-2 flex items-center justify-end gap-2 pt-4 border-t border-stone-100">
            <button
              type="button"
              onClick={onClose}
              className="border border-stone-200 px-5 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-wider text-stone-700 hover:bg-stone-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? 'Scheduling…' : 'Schedule Class'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )

  // Portal to <body> so the overlay escapes the dashboard's stacking context
  // and paints above the site's fixed header (z-50); z-[1200] clears the suite's fixed mobile bottom nav (z-100) and stays under the
  // react-select menu portal (z-1250) so dropdowns still open above the dialog.
  return typeof document === 'undefined' ? null : createPortal(modal, document.body)
}
